/**
 * History table for Screen 3 — recent auto-fires. Rows are click-through to
 * the fire detail page. Supports URL param `?highlight={id}` for deep-linking
 * from Telegram (row auto-scrolls and glows).
 */
import { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, ChevronRight, Clock, History } from 'lucide-react';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusPill } from '@/components/status-pill';
import { EmptyState } from '@/components/ui/empty';
import { Spinner } from '@/components/spinner';
import { ErrorBanner } from '@/components/error-banner';
import { relTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { AutoCampaignHistoryItem } from '@/features/auto-campaign/types';

export interface HistoryTableProps {
  items: AutoCampaignHistoryItem[] | undefined;
  isLoading: boolean;
  error: unknown;
  /** URL param `?highlight=<campaign_id>` — scrolls to & glows the matching row. */
  highlightId?: string;
  /** Limits rows shown in the preview; omit to show all fetched. */
  previewLimit?: number;
}

const STATUS_ICON: Record<string, JSX.Element> = {
  completed: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />,
  failed: <AlertTriangle className="h-3.5 w-3.5 text-destructive" />,
  executing: <Clock className="h-3.5 w-3.5 text-primary" />,
  queued: <Clock className="h-3.5 w-3.5 text-amber-500" />,
};

export function HistoryTable({
  items,
  isLoading,
  error,
  highlightId,
  previewLimit,
}: HistoryTableProps): JSX.Element {
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!highlightId || !highlightRef.current) return;
    highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, items]);

  const shown = previewLimit ? (items ?? []).slice(0, previewLimit) : items ?? [];

  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            Run history
            {items ? (
              <span className="text-xs font-normal text-muted-foreground">
                ({items.length})
              </span>
            ) : null}
          </SectionTitle>
          <SectionDescription>
            Every automatic + manual run of the auto-pilot for this project.
          </SectionDescription>
        </div>
      </SectionHeader>

      {error ? <ErrorBanner error={error} /> : null}

      {isLoading ? (
        <SkeletonRows />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={History}
          title="No runs yet"
          description="Your first digest arrives after the next scheduled run — or click Run now to start one immediately."
          variant="inline"
        />
      ) : (
        <div className="overflow-hidden rounded-md border border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">#</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden sm:table-cell">Notes</TableHead>
                <TableHead className="w-16 text-right">Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((row, idx) => {
                const highlighted = highlightId && row.id === highlightId;
                return (
                  <TableRow
                    key={row.id}
                    ref={highlighted ? highlightRef : undefined}
                    className={cn(
                      highlighted && 'bg-primary/10 hover:bg-primary/15',
                    )}
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      #{shown.length - idx}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {new Date(row.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {relTime(row.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {STATUS_ICON[row.status] ?? null}
                        <StatusPill status={row.status} />
                        {row.is_first_fire ? (
                          <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                            first
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                      {row.failure_reason ?? (row.status === 'completed' ? 'OK' : '—')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to="/campaigns/auto/fires/$fireId"
                        params={{ fireId: row.id }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                        aria-label={`Open fire ${row.id}`}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Section>
  );
}

function SkeletonRows(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-md border border-border/60 bg-card/40 p-3"
        >
          <div className="h-4 w-8 animate-pulse rounded bg-muted" />
          <div className="h-4 w-24 animate-pulse rounded bg-muted" />
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
          <Spinner className="ml-auto h-3.5 w-3.5" />
        </div>
      ))}
    </div>
  );
}
